import { throwError as observableThrowError, Observable, empty } from 'rxjs';

import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Message, MessageType, SeverityType } from 'app/notifications/message';
import { NotificationsService } from 'app/notifications/notifications.module';
import { environment } from 'environments/environment';
import { tap, catchError } from 'rxjs/operators';

const DEFAULT_REQUEST_OPTIONS: IRequestOptions = {
  headers: undefined,
  observe: 'body' as 'body',
  params: undefined,
  reportProgress: true,
  responseType: 'json' as 'json',
  withCredentials: false,
  body: undefined,
  rethrowErrors: false,
  successNotification: false,
  successNotificationMsg: false,
  errorNotification: true
};

export interface IRequestOptions {
  headers: HttpHeaders;
  observe: any;
  params: HttpParams;
  reportProgress: boolean;
  responseType: any;
  withCredentials: boolean;
  body: any;
  rethrowErrors: boolean;
  successNotification: boolean;
  successNotificationMsg: boolean;
  errorNotification: boolean;
}

export function getDefaultRequestOptions(): IRequestOptions {
  return DEFAULT_REQUEST_OPTIONS;
}

export function applicationHttpClientFactory(
  http: HttpClient,
  notificationsService: NotificationsService
) {
  return new ApplicationHttpClient(http, notificationsService);
}

@Injectable()
export class ApplicationHttpClient {
  private api = environment.WebAPIHost;
  private defaultOptions = getDefaultRequestOptions();

  /**
   * Convert Object to HttpParams
   * @param {Object} obj
   * @returns {HttpParams}
   */
  static toHttpParams(obj: Object): HttpParams {
    return Object.getOwnPropertyNames(obj).reduce((p, key) => {
      const value = obj[key];
      if (value && value !== null) {
        return p.set(key, value);
      }
      return p;
    }, new HttpParams());
  }

  /**
   * GET request
   * @param {string} endPoint end point of the api
   * @param {IRequestOptions} options options of the request like headers, body, etc.
   * @param {string} api use if there is needed to send request to different back-end than the default one.
   * @returns {Observable<T>}
   */
  public get<T>(
    endPoint: string,
    options: Partial<IRequestOptions> = this.defaultOptions,
    api?: string
  ): Observable<T> {
    return this.http.get<T>((api ? api : this.api) + endPoint, options).pipe(
      tap(
        (res: T) => {
          this.onSuccess(res, options);
        },
        (error: HttpErrorResponse) => {
          this.onError(error, options);
        }
      ),
      catchError(
        options && options.rethrowErrors
          ? this.onCatch
          : (error: any, caught: Observable<any>) => empty()
      )
    );
  }

  /**
   * POST request
   * @param {string} endPoint end point of the api
   * @param {Object} params body of the request.
   * @param {IRequestOptions} options options of the request like headers, body, etc.
   * @param {string} api use if there is needed to send request to different back-end than the default one.
   * @returns {Observable<T>}
   */
  public post<T>(
    endPoint: string,
    params: Object,
    options: Partial<IRequestOptions> = this.defaultOptions,
    api?: string
  ): Observable<T> {
    return this.http
      .post<T>((api ? api : this.api) + endPoint, params, options)
      .pipe(
        tap(
          (res: T) => {
            this.onSuccess(res, options);
          },
          (error: HttpErrorResponse) => {
            this.onError(error, options);
          }
        ),
        catchError(
          options && options.rethrowErrors
            ? this.onCatch
            : (error: any, caught: Observable<any>) => empty()
        )
      );
  }

  /**
   * PUT request
   * @param {string} endPoint end point of the api
   * @param {Object} params body of the request.
   * @param {IRequestOptions} options options of the request like headers, body, etc.
   * @param {string} api use if there is needed to send request to different back-end than the default one.
   * @returns {Observable<T>}
   */
  public put<T>(
    endPoint: string,
    params: Object,
    options: Partial<IRequestOptions> = this.defaultOptions,
    api?: string
  ): Observable<T> {
    return this.http
      .put<T>((api ? api : this.api) + endPoint, params, options)
      .pipe(
        tap(
          (res: T) => {
            this.onSuccess(res, options);
          },
          (error: HttpErrorResponse) => {
            this.onError(error, options);
          }
        ),
        catchError(
          options && options.rethrowErrors
            ? this.onCatch
            : (error: any, caught: Observable<any>) => empty()
        )
      );
  }

  /**
   * Patch request
   * @param {string} endPoint end point of the api
   * @param {Object} params body of the request.
   * @param {IRequestOptions} options options of the request like headers, body, etc.
   * @param {string} api use if there is needed to send request to different back-end than the default one.
   * @returns {Observable<T>}
   */
  public patch<T>(
    endPoint: string,
    params: Object,
    options: Partial<IRequestOptions> = this.defaultOptions,
    api?: string
  ): Observable<T> {
    return this.http
      .patch<T>((api ? api : this.api) + endPoint, params, options)
      .pipe(
        tap(
          (res: T) => {
            this.onSuccess(res, options);
          },
          (error: HttpErrorResponse) => {
            this.onError(error, options);
          }
        ),
        catchError(
          options && options.rethrowErrors
            ? this.onCatch
            : (error: any, caught: Observable<any>) => empty()
        )
      );
  }

  /**
   * DELETE request
   * @param {string} endPoint end point of the api
   * @param {IRequestOptions} options options of the request like headers, body, etc.
   * @param {string} api use if there is needed to send request to different back-end than the default one.
   * @returns {Observable<T>}
   */
  public delete<T>(
    endPoint: string,
    options: Partial<IRequestOptions> = this.defaultOptions,
    api?: string
  ): Observable<T> {
    return this.http.delete<T>((api ? api : this.api) + endPoint, options).pipe(
      tap(
        (res: T) => {
          this.onSuccess(res, options);
        },
        (error: HttpErrorResponse) => {
          this.onError(error, options);
        }
      ),
      catchError(
        options && options.rethrowErrors
          ? this.onCatch
          : (error: any, caught: Observable<any>) => empty()
      )
    );
  }

  private onCatch(error: any, caught: Observable<any>): Observable<any> {
    if (error) {
      if (error.error instanceof Error) {
        // A client-side or network error occurred. Handle it accordingly.
        return observableThrowError(error.error || 'Error');
      } else {
        // The backend returned an unsuccessful response code.
        return observableThrowError(error || 'Server error');
      }
    }
  }

  private onSuccess(res: any, options: Partial<IRequestOptions>): void {
    if (options.successNotification) {
      let title = 'Success';
      const successMsg = 'Action Completed';

      if (res) {
        title = res.statusText || title;
      }

      this.notificationsService.addNotification({
        type: MessageType.Toast,
        severity: SeverityType.Success,
        title: title,
        message: options.successNotificationMsg || successMsg
      } as Message);
    }
  }

  private onError(
    res: HttpErrorResponse,
    options: Partial<IRequestOptions>
  ): void {
    if (options.errorNotification === true) {
      const title = res.statusText || 'Error';
      let errorMsg = 'Server error\n';

      let errorMsgSupport = 'An error occurred and was logged. Please reload the page. Please contact Support if this error persists.';

      if (res.headers.get('X-Correlation-Id'))
        errorMsgSupport = `An error occurred and was logged. Please reload the page, contact Support and provide ID: ${res.headers.get('X-Correlation-Id')} to our Support team for further research`

      if (environment.production) {
        errorMsg = errorMsgSupport;
      } else {
        errorMsg = JSON.stringify(res)
      }

      this.notificationsService.addNotification({
        type: MessageType.Toast,
        severity: SeverityType.Error,
        title: title,
        message: errorMsg
      } as Message);
    }
  }

  public constructor(
    public http: HttpClient,
    private notificationsService: NotificationsService
  ) { }
}
